import { Divider, Stack, Typography } from "@mui/material";
import { fCurrency } from "../../../utils/formatNumber";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "../../../redux/store";
import { invoiceItemsForShow, invoiceTotalAmount } from "../../../helper/invoice/allCalculations";
import { calculateInvoiceAmountWithDirectionSplit } from "../utils/calculation";
import { DIRECTION_NAME } from "../../../constants";
import { getServiceDistanceInKm } from "../helper";

export default function AmountDetails({ directionType, state }) {
    const { control, getValues } = useFormContext();
    const { currentTaxRates } = useSelector(state => state.taxCore);
    const startStationId = getValues("startStation")?.id;

    const services = useWatch({ control, name: 'services' }) || [];
    const defaultServices = useWatch({ control, name: 'defaultServices' }) || [];

    const actions = useWatch({ control, name: 'actions' });
    const passengers = useWatch({ control, name: 'passengers' });

    const rawServices = services.length > 0 ? services : defaultServices;
    const distanceInKm = getServiceDistanceInKm(state);
    const selectedServices = rawServices.filter(service =>
        (!Array.isArray(service.includeOnStations) || service.includeOnStations.includes(startStationId)) &&
        (service.distanceFromKm == null && service.distanceToKm == null ||
            (distanceInKm != null &&
                (service.distanceFromKm == null || distanceInKm >= service.distanceFromKm) &&
                (service.distanceToKm == null || distanceInKm < service.distanceToKm)))
    );

    const calculated = calculateInvoiceAmountWithDirectionSplit({
        price: state.priceOneWay,
        returnPrice: state.priceReturnTicket,
        priceMonthly: state.priceMonthly,
        passengers: passengers,
        saleAction: actions[0],
        services: selectedServices,
        directionType: directionType,
        currentTaxRates: currentTaxRates,
        externalSale: state?.externalSale,
        isInternational: state?.arrCountryCode !== "RS"
    }, invoiceTotalAmount, DIRECTION_NAME);

    // Grupisani prikaz itema
    let resultItems = [];

    if (calculated.all) {
        resultItems = invoiceItemsForShow({
            price: state.priceOneWay,
            returnPrice: state.priceReturnTicket,
            priceMonthly: state.priceMonthly,
            passengers: passengers,
            directionType: directionType,
            externalSale: state?.externalSale
        });
    } else {
        const passengersSame = passengers.filter(p => !p.haveOppositeDirection);
        const passengersOpposite = passengers.filter(p => p.haveOppositeDirection);

        const sameItems = invoiceItemsForShow({
            price: state.priceOneWay,
            returnPrice: state.priceReturnTicket,
            priceMonthly: state.priceMonthly,
            passengers: passengersSame,
            directionType: directionType,
            externalSale: state?.externalSale
        });

        const oppositeItems = invoiceItemsForShow({
            price: state.priceOneWay,
            returnPrice: state.priceReturnTicket,
            priceMonthly: state.priceMonthly,
            passengers: passengersOpposite,
            directionType: directionType === DIRECTION_NAME[0] ? DIRECTION_NAME[1] : DIRECTION_NAME[0],
            externalSale: state?.externalSale
        });

        resultItems = [
            { label: directionType === DIRECTION_NAME[0] ? "Jedan smer" : "Povratna karta", items: sameItems },
            { label: directionType === DIRECTION_NAME[1] ? "Jedan smer" : "Povratna karta", items: oppositeItems }
        ];
    }
    const sameDirectionLabel =
        directionType === DIRECTION_NAME[0] ? 'Jedan smer' : 'Povratak';
    const oppositeDirectionLabel =
        directionType !== DIRECTION_NAME[0] ? 'Jedan smer' : 'Povratak'

    return (
        <Stack spacing={2} justifyContent="flex-end" width={1} sx={{ mt: 3 }}>
            {/* Displaying selected services */}
            {selectedServices.length > 0 && (
                <>
                    <Typography variant="h6" sx={{ mb: 1 }}>Dodatne usluge:</Typography>
                    {selectedServices.map(item => (
                        <Stack key={item.uid} direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1">{item.name}:</Typography>
                            <Typography variant="subtitle1" sx={{ textAlign: 'right' }}>
                                {passengers.length} x {fCurrency(item.price.toString())}
                            </Typography>
                        </Stack>
                    ))}
                    <Divider sx={{ my: 2 }} />
                </>
            )}

            {/* Displaying invoice items based on calculation type */}
            {Array.isArray(resultItems) && resultItems[0]?.label ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} justifyContent="space-between">
                    {resultItems.map((group, groupIndex) => (
                        <Stack
                            key={group.label + groupIndex}
                            spacing={1}
                            sx={{ flex: 1, minWidth: { xs: '100%', sm: '45%' } }}
                        >
                            <Typography variant="h6" sx={{ mt: groupIndex > 0 ? 2 : 0 }}>
                                {group.label}
                            </Typography>
                            <Divider />
                            {group.items.map((item, index) => (
                                <Stack key={index} direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle1">{item?.name}:</Typography>
                                    <Typography variant="subtitle1" sx={{ textAlign: 'right' }}>
                                        {item.quantity} x {fCurrency(item?.price.toString())}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    ))}
                </Stack>
            ) : (
                <Stack spacing={1}>
                    {resultItems.map((item, index) => (
                        <Stack key={index} direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1">{item?.name}:</Typography>
                            <Typography variant="subtitle1" sx={{ textAlign: 'right' }}>
                                {item.quantity} x {fCurrency(item?.price.toString())}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Total amount for payment */}
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                <Typography variant="h5">Ukupno:</Typography>

                {calculated.all && (
                    <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700, textAlign: 'right' }}>
                        {fCurrency(calculated.all.totalAmount.toString())}
                    </Typography>
                )}

                {calculated.sameDirection && calculated.oppositeDirection && (
                    <Stack direction="column" alignItems="flex-end">
                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'right' }}>
                            {fCurrency(calculated.sameDirection.totalAmount.toString())} ({sameDirectionLabel})
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'right' }}>
                            + {fCurrency(calculated.oppositeDirection.totalAmount.toString())} ({oppositeDirectionLabel})
                        </Typography>
                        <Typography
                            variant="h5"
                            color="primary.main"
                            sx={{ fontWeight: 700, textAlign: 'right', mt: 1 }}
                        >
                            ={' '}
                            {fCurrency(
                                (calculated.sameDirection.totalAmount + calculated.oppositeDirection.totalAmount).toString()
                            )}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
}
